import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import Api from "services/api";
import { isVriType } from "constants/appointmentTypes";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import toast from "react-hot-toast";
import RecordingModal from "views/JoinRoomCall/RecordingModal";
import { Video } from "lucide-react";
import { CheckinCheckoutAdjustmentModal } from "components/appointment/checkinCheckoutAdjustment";
import ViewTable from "views-interpreters/appointment/view/ViewTable";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";

const InterpreterAppointmentDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const [data, setData] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [status, setStatus] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [checkinCheckoutModal, setCheckinCheckoutModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGetAppointmentDetail = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await Api.getAppointmentDetail(token);
      if (response?.data?.message === "Data retrieved successfully.") {
        setData(response?.data?.data);
        setStatusMessage(response?.data?.message);
        setStatus(response?.data?.success);
      } else {
        setStatusMessage(response?.data?.message || "Record not found");
      }
    } catch (err) {
      console.error("Error fetching appointment details:", err);
      setStatusMessage("Error fetching details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      handleGetAppointmentDetail();
    }
  }, [token]);

  // Join Video Call handler
  const handleJoinVideoCall = async () => {
    if (
      data?.appointment_details?.video_platform === "external" &&
      data?.appointment_details?.video_link
    ) {
      try {
        const response = await Api.interpreterJoinExternalRoomWithToken(token);
        if (response?.status >= 200 && response?.status < 300) {
          const link = data.appointment_details.video_link;
          const url =
            link.startsWith("http://") || link.startsWith("https://")
              ? link
              : `https://${link}`;
          window.open(url, "_blank");
        } else {
          const errorMsg =
            response?.data?.error ||
            response?.data?.message ||
            t("appointments.failedToJoinExternalCall", "Failed to join external video call");
          toast.error(errorMsg, { className: "errorToast" });
        }
      } catch (err) {
        const link = data?.appointment_details?.video_link;
        if (link) {
          const url =
            link.startsWith("http://") || link.startsWith("https://")
              ? link
              : `https://${link}`;
          window.open(url, "_blank");
        } else {
          toast.error(
            t("appointments.errorJoiningVideoCall", "Error joining video call"),
            { className: "errorToast" }
          );
        }
      }
      return;
    }

    // Algovi (internal) video call for interpreter
    window.open(
      `/interpreter/appointment-details/join-room-call?token=${token}`,
      "_blank"
    );
  };

  // Checkin / Checkout Adjustment submit handler
  const handleAdjustmentSubmit = async (formData, endPoint, closeFormCallback) => {
    try {
      const response = await Api.actionAdjustmentWithToken(token, formData);
      if (response?.status >= 200 && response?.status < 300) {
        toast.success(
          response?.data?.message || "Appointment completed successfully.",
          { className: "successToast" }
        );
        if (closeFormCallback) closeFormCallback();
        setCheckinCheckoutModal(false);
        handleGetAppointmentDetail();
      } else {
        const errMsg =
          response?.data?.message ||
          response?.data?.error ||
          "Failed to complete appointment";
        toast.error(errMsg, { className: "errorToast" });
        if (closeFormCallback) closeFormCallback();
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred", { className: "errorToast" });
      if (closeFormCallback) closeFormCallback();
    }
  };

  const showRecordingPopup = () => {
    if (data?.appointment_details?.recording) {
      setRecordingUrl(data.appointment_details.recording);
      setShowRecordingModal(true);
    } else {
      toast.error(t("appointmentView.recordingUnavailable", "Recording not available for this appointment."), {
        className: "errorToast",
      });
    }
  };

  const AppointmentTimezone = data?.appointment_details?.timezone;
  const InterpreterTimezone = data?.interpreter?.timezone;
  const rawDatetime = data?.datetime;

  const formattedDate = AppointmentTimezone?.name
    ? moment.tz(rawDatetime, AppointmentTimezone.name).format("MMM D, YYYY")
    : rawDatetime
    ? moment(rawDatetime).format("MMM D, YYYY")
    : "N/A";

  const formattedTime = (() => {
    if (!rawDatetime) return "N/A";
    let m;
    if (AppointmentTimezone?.name && InterpreterTimezone?.name) {
      m = moment.tz(rawDatetime, AppointmentTimezone.name).tz(InterpreterTimezone.name);
    } else if (InterpreterTimezone?.name) {
      m = moment.utc(rawDatetime).tz(InterpreterTimezone.name);
    } else if (AppointmentTimezone?.name) {
      m = moment.tz(rawDatetime, AppointmentTimezone.name);
    } else {
      return moment(rawDatetime).format("hh:mm A");
    }
    const dynamicAbbr = m.format("z");
    const dynamicOffset = m.format("Z");
    const zone = dynamicAbbr ? `(${dynamicAbbr} / GMT${dynamicOffset})` : `(GMT${dynamicOffset})`;
    return `${m.format("hh:mm A")} ${zone}`;
  })();

  console.log(data?.appointment_details?.video_platform,'data' , data);
  const [timeTick, setTimeTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeTick((prev) => prev + 1);
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const showDurationAlert = useMemo(() => {
    if (!data || !data?.id || !rawDatetime) return false;

    // 1. Status check: appointment must NOT be completed or cancelled
    const appStatus = (data?.status || "").toLowerCase().trim();
    if (
      appStatus === "completed" ||
      appStatus === "cancelled" ||
      appStatus === "cancelled_by_client" ||
      appStatus === "cancel"
    ) {
      return false;
    }

    // 2. Determine target timezone
    const tzName =
      AppointmentTimezone?.name ||
      data?.appointment_details?.timezone?.name ||
      data?.accounts?.timezone?.name ||
      "America/Denver";

    // 3. Start moment in appointment timezone
    let cleanDt =
      typeof rawDatetime === "string"
        ? rawDatetime
            .replace("T", " ")
            .replace(/\..+/, "")
            .replace("Z", "")
            .trim()
        : rawDatetime;
    let startM = moment.tz(
      cleanDt,
      [
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DDTHH:mm:ss",
        "YYYY-MM-DDTHH:mm:ssZ",
      ],
      tzName
    );
    if (!startM.isValid()) {
      startM = moment.tz(rawDatetime, tzName);
    }
    if (!startM.isValid()) return false;

    // 4. Calculate end time
    let endM = null;
    if (data?.estimated_duration) {
      const durationVal = parseFloat(data.estimated_duration);
      if (!isNaN(durationVal) && durationVal > 0) {
        const unit =
          data?.estimated_duration_unit === "hour" ||
          data?.estimated_duration_unit === "hours"
            ? "hours"
            : "minutes";
        const minutes =
          unit === "hours"
            ? Math.round(durationVal * 60)
            : Math.round(durationVal);
        endM = startM.clone().add(minutes, "minutes");
      }
    }
    if (!endM && data?.end_time) {
      const dateStr = data?.date || startM.format("YYYY-MM-DD");
      let parsedEnd = moment.tz(
        `${dateStr} ${data.end_time}`,
        ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"],
        tzName
      );
      if (parsedEnd.isValid()) {
        if (parsedEnd.isBefore(startM)) {
          parsedEnd.add(1, "day"); // appointment crossed midnight
        }
        endM = parsedEnd;
      }
    }
    if (!endM) {
      // Default fallback: 1 hour duration
      endM = startM.clone().add(1, "hour");
    }

    // 5. Compare current time in appointment timezone with appointment end time
    const currentInTz = moment.tz(tzName);
    return currentInTz.isAfter(endM);
  }, [data, rawDatetime, AppointmentTimezone, timeTick]);

  console.log(data?.appointment_details?.video_platform, 'data', data);
  return (
    <>
      {statusMessage === "Data retrieved successfully." && data ? (
        <div
          className="main-resPage w-[100%]"
          style={{
            width: "100%",
            alignItems: "center",
            justifyItems: "center",
            padding: 20,
          }}
        >
          <div style={{ width: "100%", textAlign: "center" }}>
            <img
              src="/images/logo.png"
              alt="Logo"
              style={{
                width: 200,
                margin: "auto",
                alignItems: "center",
                justifyItems: "center",
              }}
            />
          </div>

          <div style={{ width: "100%" }} className="table_wrapper">
            <div className="row_main_head bg-primary-secondaryCard">
              <h2 className="main_heading main_heading1 mb-2">
                {t("appointmentView.heading", "Appointment #")} {data?.appid}
              </h2>
              <div>
                <div className="text-transform-capitalize main_heading main_heading1">
                  {data?.accounts?.user?.name && (
                    <span>
                      {t("appointmentView.client", "Client")}: {data?.accounts?.user?.name} &nbsp; | &nbsp;
                    </span>
                  )}
                  {status && (
                    <span>
                      {t("table.status", "Status")}: {data?.status}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {showDurationAlert && (
              <div
                className="row_main_head align-items-center mb-3"
                style={{
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffeeba",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                  gap: "15px",
                }}
              >
                <div className="d-flex justify-content-start" style={{ gap: "5px" }}>
                  <i
                    className="fa-solid fa-triangle-exclamation"
                    style={{ fontSize: "14px", color: "#856404", flexShrink: 0 }}
                  ></i>
                  <span style={{ fontSize: "13px", color: "#856404", fontWeight: 500, lineHeight: 1.5 }}>
                    We need some information about the duration of your assignment. Please add the details by clicking on the{" "}
                    <strong
                      role="button"
                      tabIndex={0}
                      style={{
                        textDecoration: "underline",
                        color: "#533f03",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                      onClick={() => setCheckinCheckoutModal(true)}
                    >
                      Checkin/Checkout
                    </strong>{" "}
                    option.
                  </span>
                </div>
              </div>
            )}

            {isVriType(data?.type) &&
              data?.status === "assigned" &&
              data?.appointment_details?.video_platform === "external" &&
              !data?.appointment_details?.video_link && (
                <div
                  className="rounded d-flex justify-content-center align-items-center mt-3"
                  style={{ backgroundColor: "#fcc10052" }}
                >
                  <div
                    className="standby-banner text-dark"
                    style={{ fontWeight: 600, padding: "5px 12px", fontSize: "17px" }}
                  >
                    {t("appointments.standByForVideoLink", "Stand by for video link")}
                  </div>
                </div>
              )}

            <div className="row_main_head bg-light-secondaryCard mt-3 d-flex flex-wrap">
              <h2 className="main_heading2 d-flex mb-2" toLowerCase="text-lowercase">
                <span>{t("table.date", "Date & Time")}:</span>
                <span className="dateTime viewpage_tags ms-2">{formattedDate}</span>{" "}
                <span className="dateTime viewpage_tags ms-1">{formattedTime}</span>
              </h2>
              <div className="mb-3">
                <div className="text-transform-capitalize main_heading2 d-flex align-items-center">
                  <span className="appointmentType viewpage_tags">{data?.type}</span>{" "}
                  <span className="appLanguage viewpage_tags">{data?.language?.name}</span>
                </div>
              </div>
            </div>

            {/* Action buttons (Check In / Check Out & Join Video Call) */}
            <div className="d-flex gap-2 flex-wrap justify-content-between px-2 align-items-center mt-4">
              <div className="d-flex gap-2 flex-wrap align-items-center">
                {data?.status !== "cancelled" &&
                  data?.status !== "completed" &&
                  // data?.appointment_details?.video_platform === "external" &&
                  // data?.appointment_details?.video_link
                  (
                    data?.type === "In Person" ||
                    (
                      data?.appointment_details?.video_platform === "external" &&
                      data?.appointment_details?.video_link
                    )
                  ) && (
                    <SubmitButton
                      buttonType="button"
                      className="Cancle_button"
                      btnText={t("appointments.checkinCheckout", "Check In / Check Out")}
                      handleSubmit={() => setCheckinCheckoutModal(true)}
                    />
                  )}
              </div>

              <div className="d-flex gap-2 flex-wrap align-items-center">
                {/* Join Video Call button */}
                {isVriType(data?.type) &&
                  data?.status === "assigned" &&
                  data?.status !== "completed" &&
                  (data?.appointment_details?.video_platform === "algovi" ||
                    (data?.appointment_details?.video_platform === "external" &&
                      !!data?.appointment_details?.video_link)) && (
                    <SubmitButton
                      className="green"
                      btnText={t("appointments.joinVideoCall", "Join Video Call")}
                      icon={<Video />}
                      handleSubmit={handleJoinVideoCall}
                    />
                  )}

                {/* View Recording button */}
                {data?.appointment_details?.recording && (
                  <SubmitButton
                    className="Cancle_button"
                    btnText={t("appointments.viewRecording", "View Recording")}
                    handleSubmit={showRecordingPopup}
                  />
                )}
              </div>
            </div>

            <div style={{ width: "100%" }} className="px-2 mt-3">
              <ViewTable item={data} />
            </div>
          </div>
        </div>
      ) : statusMessage === "Page Expired" ? (
        <div className="error-page-container">
          <img src="/images/logo.png" alt="" />
          <h4>OOps! {statusMessage}</h4>
          <Link to="/" className="main_button mt-5">
            Go to home page
          </Link>
        </div>
      ) : (
        <div className="error-page-container">
          <img src="/images/logo.png" alt="" />
          <h4>Oopps! The page you were looking for doesn't exist.</h4>
          <p>
            <Link to="/" className="button">
              Go to home page
            </Link>
          </p>
        </div>
      )}

      <CheckinCheckoutAdjustmentModal
        isOpen={checkinCheckoutModal}
        handleClose={() => setCheckinCheckoutModal(false)}
        onSubmit={handleAdjustmentSubmit}
        item={data}
        targetTimezone={
          data?.interpreter?.timezone?.name || data?.appointment_details?.timezone?.name
        }
      />

      <RecordingModal
        recordingUrl={recordingUrl}
        showModal={showRecordingModal}
        onClose={() => setShowRecordingModal(false)}
      />
    </>
  );
};

export default InterpreterAppointmentDetails;
